/**
 * Cross-tab reveal context for source citations: lets any descendant (a
 * Blueprint card's inline citation, a chat-review row) jump to the cited source
 * — activates 2-pane split mode, mounts Sources in Pane 2 while preserving Pane 1,
 * opens the source, scrolls the cited span into view, and flashes it — without
 * prop-drilling.
 *
 * Mirrors {@link RevealCardContext} (which reveals Blueprint cards). The context
 * value is a callback; {@link AppContent} provides it and owns the navigation +
 * reveal state. Components consume it with {@link useRevealSource}.
 * Nothing is logged (HIPAA).
 */
import { createContext, useContext } from "react";
import type { SourceRevealTarget } from "./blueprint/citationResolve";

/** Signature of the source-reveal callback. */
export type RevealSourceFn = (target: SourceRevealTarget) => void;

/**
 * Context carrying the source-reveal callback. Defaults to a no-op so consumers
 * outside a provider still compile and run without errors.
 */
export const RevealSourceContext = createContext<RevealSourceFn>(
  () => undefined,
);

/**
 * Consume the source-reveal callback.
 *
 * @returns The callback from the nearest {@link RevealSourceContext} provider.
 */
export function useRevealSource(): RevealSourceFn {
  return useContext(RevealSourceContext);
}
